import { useState, useCallback, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { selectCurrentFolderFiles } from '@/user_files/files/store/selectors'
import { isExternalStorageSyncedFolder, selectCurrentFolderChildren } from '@/user_files/folders/store/selectors'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { GlobalToastType, kToastDurationShortMs } from '@/constants/constants-ui'
import { UserFilesActions } from '@/user_files/files/store/slice'
import { UserFoldersActions } from '@/user_files/folders/store/slice'
import { renameFile } from '@/user_files/files/fetch/patch-rename-file'
import { renameFolder } from '@/user_files/folders/fetch/patch-rename-folder'
import type { UserFileRead } from '@/uppy-file-upload/schemas'
import type { FolderResponse } from '@/user_files/folders/schemas'
import { getUserFilesErrorMessage, isUserFilesNameConflict, shouldRefreshAfterUserFilesError } from '@/user_files/utils/api-error'
import { buildFileRenamePrecondition, buildFolderRenamePrecondition } from '@/user_files/utils/preconditions'
import type { FileRenamePrecondition, FolderRenamePrecondition } from '@/user_files/schemas/preconditions'
import { UppyFileUploadActions } from '@/uppy-file-upload/store/slice'

export type RenameItem =
  | { type: 'file'; id: string; name: string; folderId: string; precondition: FileRenamePrecondition }
  | { type: 'folder'; id: string; name: string; parentFolderId: string | null; precondition: FolderRenamePrecondition }

export function useRenameDialog() {
  const dispatch = useAppDispatch()
  const files = useAppSelector(selectCurrentFolderFiles)
  const folders = useAppSelector(selectCurrentFolderChildren)

  const [item, setItem] = useState<RenameItem | null>(null)
  const [isRenaming, setIsRenaming] = useState(false)
  const [renameError, setRenameError] = useState<string | null>(null)

  const openRenameForFile = useCallback((file: UserFileRead) => {
    setItem({ type: 'file', id: file.id, name: file.original_filename, folderId: file.folder_id, precondition: buildFileRenamePrecondition(file) })
    setRenameError(null)
  }, [])

  const openRenameForFolder = useCallback((folder: FolderResponse) => {
    setItem({ type: 'folder', id: folder.id, name: folder.name, parentFolderId: folder.parent_id, precondition: buildFolderRenamePrecondition(folder) })
    setRenameError(null)
  }, [])

  const closeRenameDialog = useCallback(() => {
    setItem(null)
    setRenameError(null)
  }, [])

  const confirmRename = useCallback(
    async (newName: string) => {
      if (!item) return

      setIsRenaming(true)
      setRenameError(null)

      try {
        if (item.type === 'file') {
          const updated = await renameFile(item.id, { filename: newName, precondition: item.precondition })
          dispatch(UserFilesActions.upsertUserFiles([updated]))
          dispatch(UppyFileUploadActions.fileRenamedByServerRecord(updated))
          dispatch(UserFilesActions.fetchFilesForFolder({ folderId: updated.folder_id }))
        } else {
          const updated = await renameFolder(item.id, { name: newName, precondition: item.precondition })
          dispatch(UserFoldersActions.upsertFolder(updated))
          if (updated.parent_id) {
            dispatch(UserFoldersActions.fetchFolderChildren({ folderId: updated.parent_id, force: true }))
          }
        }
        dispatch(openGlobalToast({ type: GlobalToastType.SUCCESS, message: 'Renamed 1 item', durationMs: kToastDurationShortMs }))
        closeRenameDialog()
      } catch (err) {
        setRenameError(getUserFilesErrorMessage(err, 'Failed to rename'))
        const shouldRefresh = shouldRefreshAfterUserFilesError(err) || !isUserFilesNameConflict(err)
        if (item.type === 'file' && shouldRefresh) {
          dispatch(UserFilesActions.fetchFilesForFolder({ folderId: item.folderId }))
        } else if (item.type === 'folder' && item.parentFolderId && shouldRefresh) {
          dispatch(UserFoldersActions.fetchFolderChildren({ folderId: item.parentFolderId, force: true }))
        }
      } finally {
        setIsRenaming(false)
      }
    },
    [item, dispatch, closeRenameDialog]
  )

  // exclude DMS folders from the existing folder names
  const occupiedNames = useMemo(() => {
    const names =
      item?.type === 'file'
        ? new Set(files.map((f) => f.original_filename))
        : new Set(folders.filter((folder) => !isExternalStorageSyncedFolder(folder)).map((f) => f.name))
    if (item?.name) names.delete(item.name)
    return names
  }, [files, folders, item])

  return {
    renameDialog: { open: item !== null, item },
    openRenameForFile,
    openRenameForFolder,
    closeRenameDialog,
    confirmRename,
    isRenaming,
    renameError,
    occupiedNames,
  }
}
